import { useEffect, useRef, type ReactNode } from 'react';
import { hasFinePointer } from './useReducedMotion';

export interface MagneticProps {
  children: ReactNode;
  /** Share of the pointer offset that the element follows (0–1). */
  strength?: number;
  /** Maximum travel in px. */
  max?: number;
  className?: string;
}

/** Wraps a CTA so it eases a few px toward the pointer on hover (fine pointers only; springs back on leave). */
export default function Magnetic({ children, strength = 0.28, max = 8, className = '' }: MagneticProps) {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !hasFinePointer()) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let frame = 0;
    let px = 0;
    let py = 0;
    const clamp = (v: number) => Math.max(-max, Math.min(max, v));

    const apply = () => {
      frame = 0;
      const rect = el.getBoundingClientRect();
      const dx = px - (rect.left + rect.width / 2);
      const dy = py - (rect.top + rect.height / 2);
      el.style.transform = `translate3d(${clamp(dx * strength).toFixed(1)}px, ${clamp(dy * strength).toFixed(1)}px, 0)`;
    };
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
      px = e.clientX;
      py = e.clientY;
      el.classList.add('is-pulling');
      if (!frame) frame = requestAnimationFrame(apply);
    };
    const onLeave = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      el.classList.remove('is-pulling');
      el.style.transform = '';
    };

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', onLeave);
    return () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [strength, max]);

  return (
    <span ref={ref} className={`magnetic ${className}`}>
      {children}
    </span>
  );
}
